import moment from "moment";
import Paper from "components/general/PaperComponent";
import Text from "components/general/Text";
import UserInfo from "components/cards/UserInfo";
import ModalWrapper from "components/modals/ModalWrapper";
import { useUnassignPunchMutation } from "apis/services/timekeeper";
import { AllStaffShiftsProps } from "apis/types/shift";
import {
  BOUNDARY_LABELS,
  BOUNDARY_VALUE_BY_KEY,
  PunchBoundaryKey,
} from "apis/types/timekeeper";
import showSuccessMsg from "utils/showSuccessMsg";
import getBackendError from "utils/getBackendError";
import formatTime from "utils/formatDateTime";
import COLORS from "constants/Colors";
import { DATE_TIME_FORMAT } from "./helpers";
import styles from "./styles.module.scss";

interface UnassignConfirmProps {
  isVisible: boolean;
  setIsVisible: (state: boolean) => void;
  shift?: AllStaffShiftsProps | null;
  boundary: PunchBoundaryKey | null;
  punchTime?: string | null;
  punchId?: number | null;
}

export default function UnassignConfirm({
  isVisible,
  setIsVisible,
  shift,
  boundary,
  punchTime,
  punchId,
}: UnassignConfirmProps) {
  const [unassignPunch, { isLoading, error, reset }] =
    useUnassignPunchMutation();

  const boundaryLabel = boundary ? BOUNDARY_LABELS[boundary] : "";

  const onConfirm = () => {
    // Guarding on isLoading keeps a double click from firing two requests.
    if (!shift?.id || !boundary || isLoading) return;
    reset();
    unassignPunch({
      shift_id: shift.id,
      boundary: BOUNDARY_VALUE_BY_KEY[boundary],
    })
      .unwrap()
      .then(() => {
        showSuccessMsg({ msg: `${boundaryLabel} has been unassigned!` });
        // The mutation invalidates Timekeeper, so the punches modal behind this
        // one refetches the shift and picks the new boundaries up on its own.
        setIsVisible(false);
      })
      // The error middleware toasts it, the inline message below keeps it up.
      .catch(() => {});
  };

  const assignedPunch = () => {
    if (!punchTime && !punchId) return "-";
    const time = punchTime ? moment(punchTime).format(DATE_TIME_FORMAT) : "-";
    return punchId ? `${time} (Punch #${punchId})` : time;
  };

  const details = [
    {
      title: "Boundary",
      value: boundaryLabel || "-",
    },
    {
      title: "Assigned Punch",
      value: assignedPunch(),
    },
    {
      title: "Shift Date",
      value: shift?.date ? moment(shift.date).format("DD-MM-YYYY") : "-",
    },
    {
      title: "Scheduled Time",
      value:
        shift?.start_time && shift?.end_time
          ? `${formatTime(shift.start_time)} - ${formatTime(shift.end_time)}`
          : "-",
    },
  ];

  const consequences = [
    "The manual assignment on this boundary is removed.",
    "The punch itself stays in the raw punch repository.",
    "The opposite boundary is left exactly as it is.",
    "The shift is recalculated, so it may end up missing this boundary or pick up another valid punch.",
  ];

  const errorMessage = getBackendError(error);

  return (
    <ModalWrapper
      headerTitle={`Unassign ${boundaryLabel}`}
      size="large"
      isVisible={isVisible}
      setIsVisible={setIsVisible}
      onConfirm={onConfirm}
      isLoading={isLoading}
      isBtnDisabled={!shift?.id || !boundary || isLoading}
    >
      <div className={styles.container}>
        <Paper
          backgroundColor={COLORS.light}
          borderColor={COLORS.lightGreyBackground}
          paddingVertical={24}
          paddingHorizontal={28}
        >
          <UserInfo data={shift?.employee} isTableCol />
        </Paper>

        <div className={styles.gridRow}>
          {details.map((item) => (
            <div key={item.title} className={styles.detailCard}>
              <Text className={styles.detailTitle}>{item.title}</Text>
              <Text className={styles.detailValue}>{item.value}</Text>
            </div>
          ))}
        </div>

        <div className={styles.recordedSection}>
          <Text className={styles.sectionHeader}>What happens next</Text>
          <ul className={styles.consequenceList}>
            {consequences.map((item) => (
              <li key={item}>
                <Text className={styles.consequenceText}>{item}</Text>
              </li>
            ))}
          </ul>
        </div>

        {!!errorMessage && (
          <Text className={styles.errorText}>{errorMessage}</Text>
        )}
      </div>
    </ModalWrapper>
  );
}
